import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import {
  ArrowRightIcon,
  ClockIcon,
  EyeOffIcon,
  SearchIcon,
  ShieldCheckIcon
} from '@heroicons/react/solid'
import { AffiliateContactForm } from '@components/AffiliateContactForm'
import { BullseyeIcon } from '@components/UI/Icons/bullseye'
import { TagIcon } from '@components/UI/Icons/tag'
import { BoltIcon } from '@components/UI/Icons/bolt'
import { MoneyBillIcon } from '@components/UI/Icons/moneyBill'

export default function HomeInspectors() {
  return (
    <>
      <NextSeo
        title="Majordomo's Home Inspector Affiliate Program"
        description="Earn additional income while we help you guide your clients to the next steps by finding repair estimates based on the results of your home inspection."
        canonical="https://majordomo.com/home-inspector-affiliate"
        openGraph={{
          url: 'https://majordomo.com/home-inspector-affiliate',
          title: "Majordomo's Home Inspector Affiliate Program | Majordomo",
          description:
            'Earn additional income while we help you guide your clients to the next steps by finding repair estimates based on the results of your home inspection.',
          images: [
            {
              url: 'https://majordomo.com/images/product-pages/inspector-2.png',
              width: 700,
              height: 700,
              alt: 'Home Inspector standing on the roof of a home'
            },
            {
              url: 'https://majordomo.com/images/product-pages/domoreport.png',
              width: 1816,
              height: 1992,
              alt: 'Screen shot of Majordomo’s Domoreport'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <SearchIcon
                width={36}
                height={36}
                className="pr-2 mr-2 border-r border-gray-200 text-brand"
              />
              <h1 className="text-2xl font-black tracking-wide text-brand uppercase">
                Home Inspector Affiliate Program
              </h1>
            </div>

            <p className="lg:mb-8 md:mb-4 mb-8 lg:text-2xl md:text-lg text-xl lg:max-w-lg sm:max-w-sm max-w-xs font-light">
              Answer the <span className="font-black">#1 question</span> after
              the home inspection:{' '}
              <span className="font-black">
                "How much will that repair cost?"
              </span>
            </p>

            <p className="w-full max-w-lg md:p-0 px-8">
              As a home inspector, you get it all the time after the inspection
              is done: "How much will that repair cost?" Now, you don't have to
              avoid your client's biggest concern because of regulatory
              requirements. You can send them to Majordomo, where they'll
              receive ZIP Code specific repair estimates based on your
              inspection.
            </p>
          </div>

          <img
            src="/images/product-pages/inspector-2.png"
            className="md:order-last order-first md:mb-0 mb-8 folded-l-hero"
            width="50%"
          />
        </div>

        <div className="lg:mb-36 mb-12">
          <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-0 pb-8 mx-auto">
            Help your clients take action after the home inspection
          </h4>

          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16">
              <img
                src="/images/product-pages/domoreport.png"
                alt="Majordomo product samples"
                className="2xl:max-w-xl xl:max-w-md lg:max-w-md max-w-xs"
              />
            </div>

            <div className="space-y-12 xl:p-16 md:p-8 p-4 lg:w-1/2 w-full flex flex-col items-center">
              <div className="flex relative">
                <BullseyeIcon className="absolute top-0 xl:-left-24 w-16 h-16 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 xl:mt-0 mt-20">
                    Precise local estimates, reviewed by humans
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Majordomo's repair-estimate data comes from analysts who
                    study contractor and insurance invoices for 20,000 projects
                    from 35,000 providers for material, equipment, labor and
                    installation costs across the US. This lets us create highly
                    accurate localized estimates for a specific market.{' '}
                    <a
                      className="text-accentBlue hover:underline"
                      href="/blog/majordomos-repair-cost-estimates-are-local-and-accurate/"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <TagIcon className="absolute top-0 xl:-left-24 w-16 h-16 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 xl:mt-0 mt-20">
                    No more "Deal-Killer" labels
                  </h4>
                  <p className="max-w-lg mr-auto">
                    You've been called a "deal killer" for uncovering problems
                    that scare buyers away. The Domoreport helps buyers focus on
                    the solution and inspires them to move forward.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <BoltIcon className="absolute top-0 xl:-left-24 w-16 h-16 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 xl:mt-0 mt-20">
                    Help your clients negotiate the best deal
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Empower your clients and their agents with the request list:
                    an integrated tool for asking for repairs and credits, base
                    on your inspection.
                  </p>
                </div>
              </div>

              <div className="flex relative self-start 2xl:ml-8 lg:ml-0 md:ml-12 sm:ml-8 ml-0">
                <h4 className="max-w-lg md:text-2xl text-lg font-black tracking-tight mb-2">
                  All for just $99
                  <span className="font-light text-base"> / per report</span>
                </h4>
              </div>
            </div>
          </div>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0 md:pt-0 pt-8">
            <Link href="/checkout">
              <Button variant="primary" clipSide="right">
                Get repair estimates{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </Link>

            <Link href="/features/domoreport-repair-estimates">
              <a className="flex items-center justify-center font-bold text-accentBlue hover:underline">
                Learn More{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </a>
            </Link>
          </div>
        </div>

        <div className="bg-gray-50 lg:mb-36 mb-12 py-12">
          <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-0 pb-8 mx-auto">
            A partnership to help you scale
          </h4>

          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="space-y-8 lg:p-16 p-4 lg:w-1/2 w-full flex flex-col items-center">
              <div className="flex-relative pb-4">
                <p className="text-lg font-black font-heading max-w-lg">
                  Whether you’re an established inspector or just starting,
                  Majordomo’s free affiliate program gives you the tools and
                  resources you need
                </p>
              </div>

              <div className="flex relative">
                <MoneyBillIcon
                  size={16}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2"
                />
                <div>
                  <h4 className="max-w-lg mr-auto text-lg font-black tracking-tight mb-1 md:mt-0 mt-16">
                    Join for free and earn additional revenue
                  </h4>
                  <p className="max-w-sm mr-auto">
                    Sign up for free. Then attach a customized PDF flyer to each
                    inspection report. When a client clicks through and buys a
                    Domoreport, you earn a commission.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <EyeOffIcon
                  width={16}
                  height={16}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto text-lg font-black tracking-tight mb-1 md:mt-0 mt-16">
                    Respecting client privacy
                  </h4>
                  <p className="max-w-sm mr-auto">
                    We only see - and protect - the client’s info that they
                    choose to share with us. They won’t get unsolicited calls
                    from contractors.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <ShieldCheckIcon
                  width={16}
                  height={16}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2"
                />
                <div>
                  <h4 className="max-w-lg mr-auto text-lg font-black tracking-tight mb-1 md:mt-0 mt-16">
                    Proper disclosure
                  </h4>
                  <p className="max-w-sm mr-auto">
                    We’re upfront with the client that you may receive an
                    affiliate commission when they purchase from us.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <ClockIcon
                  width={16}
                  height={16}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2"
                />
                <div>
                  <h4 className="max-w-lg mr-auto text-lg font-black tracking-tight mb-1 md:mt-0 mt-16">
                    Priority support
                  </h4>
                  <p className="max-w-sm mr-auto">
                    Majordomo is always ready to help with 24-hour chat and
                    customer support.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center lg:w-1/2 w-full lg:p-16">
              <AffiliateContactForm
                action="https://majordomo.us19.list-manage.com/subscribe/post?u=0d8c6654c6ad1fe275a334234&amp;id=c8f1438cad"
                title="Learn More"
                referrer="For Home Inspectors - Learn More"
                redirect={true}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:pb-36 pb-12 sm:px-0 px-8">
          <div className="flex lg:flex-row flex-col">
            <div className="xl:max-w-lg max-w-md lg:w-1/2 w-full m-auto text-center lg:mb-auto lg:order-first order-last">
              <h4 className="md:text-2xl text-lg font-black tracking-tight mb-8">
                "This is a really cool service. If I were a home inspector today
                I'd be hooked up with this. I would tell all of my real estate
                agents to use this!"
              </h4>
              <p>
                <span className="font-black">Ben Gromicko</span>,
                <br />
                InterNACHI COO,
                <br />
                Chief Academic Officer
                <br />
              </p>
            </div>

            <div className="flex items-center justify-center lg:w-1/2 w-full lg:m-0 mb-8 lg:order-last order-first">
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/WXtUZ0oUJco"
                frameBorder="0"
                className="rounded-md shadow-md"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col bg-gray-50 lg:mb-36 mb-12 py-16">
          <div>
            <h4 className="max-w-lg m-auto md:text-2xl text-lg text-center font-black tracking-tight mb-2">
              Our Affiliations
            </h4>
            <p className="text-center md:max-w-lg max-w-sm m-auto sm:px-0 px-8">
              Majordomo is proud to be affiliated with InterNACHI and ASHI. Both
              international organizations aspire to improve home inspections,
              the overall home buying & ownership experience
            </p>
          </div>

          <div className="flex md:flex-row flex-col">
            <div className="flex flex-col justify-center md:w-1/2 w-full md:px-16 p-8">
              <img
                src="/images/product-pages/internachi-logo.png"
                className="w-40 self-center"
              />

              <a
                href="https://www.nachi.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="md:text-2xl text-lg text-accentBlue font-black hover:underline cursor-pointer">
                  InterNACHI
                </h4>
              </a>

              <p>
                The world’s leading association for home inspectors with free
                training, online certification, business development, and more.
              </p>
            </div>

            <div className="flex flex-col justify-center md:w-1/2 w-full md:px-16 p-8">
              <img
                src="/images/product-pages/ashi-logo.png"
                className="w-36 self-center"
              />

              <a
                href="https://www.homeinspector.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="md:text-2xl text-lg text-accentBlue font-black hover:underline cursor-pointer">
                  ASHI
                </h4>
              </a>

              <p>
                Committed to progressing the home inspection profession and
                providing resources to promote healthy home ownership practices.
              </p>
            </div>
          </div>
        </div>

        <div className="lg:mb-36 mb-12">
          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="space-y-4 lg:p-16 p-4 lg:w-1/2 w-full flex flex-col lg:order-first order-last">
              <div>
                <h4 className="md:text-2xl text-lg font-black tracking-tight">
                  100% satisfaction
                </h4>
                <h4 className="md:text-2xl text-lg font-black tracking-tight">
                  60-day money-back guarantee
                </h4>
              </div>

              <p>
                If you don't love the Domoreport, we'll return 100% of your
                money. But we're confident you'll love it. Here’s why:
              </p>

              <p>
                Majordomo's repair-estimate data comes from analysts who study
                contractor and insurance invoices for 20,000 projects from
                35,000 providers for material, equipment, labor and installation
                costs across the US. This lets us create highly accurate
                localized estimates for your specific market.{' '}
                <a
                  href="/blog/majordomos-repair-cost-estimates-are-local-and-accurate/"
                  className="text-accentBlue hover:underline"
                >
                  Learn More
                </a>
              </p>

              <p>
                We invite you to use the Domoreport with your home inspection
                and receive repair estimates within 24 hours. We're confident
                you'll be impressed with its completeness, readability and
                accuracy.
              </p>

              <p>
                So here's our offer: If you don’t love the Domoreport or if the
                estimates aren't accurate, we'll refund 100% of your money. You
                have nothing to lose by giving it a try.
              </p>
            </div>

            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16 lg:order-last order-first">
              <img
                src="/images/product-pages/satisfaction.png"
                className="lg:max-w-md max-w-xs"
              />
            </div>
          </div>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0 md:pt-0 pt-8">
            <Link href="/contact-us">
              <Button variant="primary" clipSide="right">
                Contact us to learn more{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
